import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

export interface RecentItem {
  id: string;
  title: string;
  subtitle?: string;
  path: string;
  type: 'object' | 'workflow' | 'field' | 'setting' | 'record' | 'page';
  icon: 'Package' | 'Zap' | 'FileText' | 'Settings' | 'File' | 'Layout';
  timestamp: number;
}

const STORAGE_KEY = 'crm_recent_items';
const MAX_RECENT_ITEMS = 10;

interface RecentItemsContextValue {
  recentItems: RecentItem[];
  addRecentItem: (item: Omit<RecentItem, 'timestamp'>) => void;
  clearRecentItems: () => void;
  removeRecentItem: (id: string) => void;
  getRelativeTime: (timestamp: number) => string;
}

const RecentItemsContext = createContext<RecentItemsContextValue | undefined>(undefined);

export const RecentItemsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [recentItems, setRecentItems] = useState<RecentItem[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return JSON.parse(stored);
    } catch (error) {
      console.error('Failed to load recent items:', error);
    }
    return [];
  });

  // Save to localStorage whenever items change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(recentItems));
    } catch (error) {
      console.error('Failed to save recent items:', error);
    }
  }, [recentItems]);

  const addRecentItem = useCallback((item: Omit<RecentItem, 'timestamp'>) => {
    setRecentItems(prev => {
      const filtered = prev.filter(i => i.id !== item.id);
      return [
        { ...item, timestamp: Date.now() },
        ...filtered
      ].slice(0, MAX_RECENT_ITEMS);
    });
  }, []);

  const clearRecentItems = useCallback(() => {
    setRecentItems([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const removeRecentItem = useCallback((id: string) => {
    setRecentItems(prev => prev.filter(item => item.id !== id));
  }, []);

  const getRelativeTime = (timestamp: number): string => {
    const now = Date.now();
    const diff = now - timestamp;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) return 'Just now';
    if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  return (
    <RecentItemsContext.Provider
      value={{
        recentItems,
        addRecentItem,
        clearRecentItems,
        removeRecentItem,
        getRelativeTime
      }}
    >
      {children}
    </RecentItemsContext.Provider>
  );
};

export const useRecentItems = () => {
  const context = useContext(RecentItemsContext);
  if (context === undefined) {
    throw new Error('useRecentItems must be used within a RecentItemsProvider');
  }
  return context;
};
